import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RevenueTracker = ({ stats }) => {
  // Mock hourly revenue data
  const hourlyData = [
    { hour: '06:00', revenue: 0 },
    { hour: '08:00', revenue: 240 },
    { hour: '10:00', revenue: 480 },
    { hour: '12:00', revenue: 360 },
    { hour: '14:00', revenue: 720 },
    { hour: '16:00', revenue: 840 },
    { hour: '18:00', revenue: 960 },
    { hour: '20:00', revenue: 600 },
    { hour: '22:00', revenue: 240 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg shadow-elevation-3 p-3">
          <p className="text-sm font-medium text-card-foreground">
            {label}
          </p>
          <p className="text-sm text-success">
            Receita: R$ {payload?.[0]?.value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-heading font-bold text-card-foreground mb-1">
            Receita de Hoje
          </h3>
          <p className="text-sm text-muted-foreground">
            Acompanhamento em tempo real
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-heading font-bold text-success">
            R$ {stats?.dailyRevenue?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </div>
          <div className="flex items-center space-x-1 text-sm text-success">
            <Icon name="TrendingUp" size={14} />
            <span>+12.5% vs ontem</span>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="h-64 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={hourlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
            <XAxis 
              dataKey="hour" 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickFormatter={(value) => `R$ ${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="hsl(var(--success))"
              strokeWidth={3}
              dot={{ fill: 'hsl(var(--success))', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: 'hsl(var(--success))', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg mx-auto mb-2">
            <Icon name="Clock" size={16} className="text-primary" />
          </div>
          <div className="text-lg font-heading font-bold text-card-foreground">
            18:00
          </div>
          <div className="text-xs text-muted-foreground">Pico de receita</div>
        </div>

        <div className="text-center p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 bg-warning/10 rounded-lg mx-auto mb-2">
            <Icon name="Target" size={16} className="text-warning" />
          </div>
          <div className="text-lg font-heading font-bold text-card-foreground">
            87%
          </div>
          <div className="text-xs text-muted-foreground">Meta diária</div>
        </div>

        <div className="text-center p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-lg mx-auto mb-2">
            <Icon name="Calculator" size={16} className="text-success" />
          </div>
          <div className="text-lg font-heading font-bold text-card-foreground">
            R$ 190
          </div>
          <div className="text-xs text-muted-foreground">Ticket médio</div>
        </div>
      </div>

      {/* Payment Status */}
      <div className="mt-6 pt-4 border-t border-border">
        <h4 className="font-heading font-semibold text-card-foreground mb-3">
          Status de Pagamentos
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-success"></div>
              <span className="text-sm text-muted-foreground">Pagos</span>
            </div>
            <div className="text-sm font-medium text-card-foreground">
              R$ 2.410,00 (12 reservas)
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-warning"></div>
              <span className="text-sm text-muted-foreground">Pendentes</span>
            </div>
            <div className="text-sm font-medium text-card-foreground">
              R$ 440,50 (3 reservas)
            </div>
          </div>

          {/* Payment Progress Bar */}
          <div className="mt-3">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Pagamentos recebidos</span>
              <span>84.5%</span>
            </div>
            <div className="w-full bg-muted/30 rounded-full h-2">
              <div 
                className="bg-success h-2 rounded-full transition-all duration-300" 
                style={{ width: '84.5%' }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueTracker;